import { setDefaultOptions } from 'date-fns'

const locales = {
  cs: () => import('date-fns/locale/cs'),
  de: () => import('date-fns/locale/de'),
  it: () => import('date-fns/locale/it')
} as const

const messages = {
  cs: () => import('../lang/cs.json'),
  en: () => import('../lang/en.json'),
  de: () => import('../lang/de.json'),
  it: () => import('../lang/it.json')
} as const

const routes = {
  en: () => import('../lang/routes.en.json'),
  cs: () => import('../lang/routes.cs.json'),
  de: () => import('../lang/routes.de.json'),
  it: () => import('../lang/routes.it.json')
} as const

const localeStrings = {
  en: 'EN',
  cs: 'CZ',
  de: 'DE',
  it: 'IT'
} as const

const countryCodes = {
  en: 'GB',
  cs: 'CZ',
  de: 'DE',
  it: 'IT'
} as const

export type LocaleKey = keyof typeof localeStrings

export const dayFormatStrings = {
  en: 'yyyy.MM.dd'
} as const

export const dayTimeFormatStrings = {
  en: 'yyyy.MM.dd H.mm'
} as const

export function localeToString (locale: LocaleKey) {
  return localeStrings[locale]
}

export function countryCode (locale: keyof typeof countryCodes) {
  return countryCodes[locale]
}

export async function setLocale (locale: LocaleKey) {
  if (locale !== 'en') {
    const dateLocale = (await getLocale(locale)).default
    setDefaultOptions({ locale: dateLocale })
  }
}

export async function getMessages (locale: keyof typeof messages) {
  const result = await Promise.all([messages[locale](), routes[locale]()])
  return { ...result[0], ...result[1] }
}

export async function getMessagesLocale (locale: keyof typeof messages) {
  const result = await Promise.all([messages[locale]()])
  return { ...result[0], ...result[1] }
}

export function getLocale (locale: keyof typeof locales) {
  return locales[locale]()
}

export function getFormatString (locale: 'en', type = 'day') {
  if (type === 'day') {
    return dayFormatStrings[locale]
  }
  if (type === 'dayTime') {
    return dayTimeFormatStrings[locale]
  }
}

export const getDecimalSeparator = (locale: LocaleKey) => {
  return (1.1).toLocaleString(locale).substring(1, 2)
}

export const formatDecimalByLocale = (
  value: number | string | null | undefined,
  locale: LocaleKey
) => {
  const decimalSeparator = getDecimalSeparator(locale)

  if (value === null || value === undefined || value === '' || isNaN(Number(value))) {
    return ''
  }

  return value.toString().replace('.', decimalSeparator)
}
